/**
 * engine/datas.ts — aritmética de mês (tipo `Ym`) e arredondamento.
 * Cópia literal de `fincast-viabilidade-v67.html`, linhas 1322-1326.
 *
 * Nenhuma destas funções usa relógio: `ymOf` NÃO cria `Date`, portanto é imune a fuso
 * horário. Manter assim (o motor roda no browser e no servidor com o mesmo resultado).
 * `addDays` cria `Date`, mas só em UTC — imune a fuso desde 29/07/2026 (ver a nota nela).
 */

import type { IsoDate, Ym } from './tipos';

/**
 * v67 linha 1322: `const teto=Math.ceil;`
 * Usado em `vendExig`/`finExig`. NÃO trocar por `Math.round` — mudaria o mínimo de
 * unidades exigidas.
 */
export const teto: (x: number) => number = Math.ceil;

/**
 * v67 linha 1323. `'2026-09-15'` → 2026*12 + 8.
 * Devolve `null` quando `d` é falsy (string vazia inclusive).
 */
export function ymOf(d: IsoDate | '' | null | undefined): Ym | null {
  if (!d) return null;
  const [y, m] = d.split('-').map(Number);
  return y * 12 + (m - 1);
}

/**
 * v67 linha 1324 — formato 'mmm.aa' (ex.: 'jul.26'), meses PT-BR hardcoded.
 * É APRESENTAÇÃO: só está aqui porque `calcViab` monta frases. Ver `formato.ts`.
 */
export function ymLabel(ym: Ym): string {
  const y = Math.floor(ym / 12), m = ym % 12;
  return ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'][m] + '.' + String(y).slice(2);
}

/** v67 linha 1325. `m0` é mês 0-based (janeiro = 0). */
export function ymFromParts(y: number, m0: number): Ym {
  return y * 12 + m0;
}

/**
 * v67 linha 1326 — soma de dias de CALENDÁRIO. `addDays('2026-07-01', 30)`→`'2026-07-31'`.
 *
 * ⚠ CORRIGIDA em 29/07/2026 (pré-requisito da Parte B do IGH). O protótipo fazia
 * `new Date(d+'T00:00:00')` (nasce em horário LOCAL) e devolvia `toISOString()` (UTC): a
 * LESTE de Greenwich a meia-noite local é o dia ANTERIOR em UTC, e o `slice(0,10)` cortava
 * um dia — `addDays(d, 0)` chegava a devolver o dia anterior e `addDays(d, 1)` virava NO-OP.
 * A OESTE (Brasil, UTC-3) e em UTC acertava, e é por isso que o defeito nunca apareceu.
 *
 * Agora a aritmética é toda em UTC, sem passar por horário local: o resultado é o MESMO em
 * qualquer fuso — condição para o motor rodar no navegador e no servidor com o mesmo
 * número. Nos fusos em que o produto roda hoje a saída é idêntica à de antes.
 *
 * Mantém o contrato de falhar alto: data inválida estoura `RangeError` ("Invalid time
 * value"), como no protótipo.
 */
export function addDays(d: IsoDate, n: number): IsoDate {
  const [y, m, dia] = String(d).split('-').map(Number);
  const dt = new Date(Date.UTC(y, (m as number) - 1, dia));
  dt.setUTCDate(dt.getUTCDate() + n);
  return dt.toISOString().slice(0, 10);
}

/** O 1º dia do mês `ym`, em ISO (`24318` → `'2026-07-01'`). */
export function primeiroDia(ym: Ym): IsoDate {
  const y = Math.floor(ym / 12), m = ym % 12;
  return `${String(y).padStart(4, '0')}-${String(m + 1).padStart(2, '0')}-01`;
}

/** Domingo de Páscoa (algoritmo gregoriano anônimo), como [mês 0-based, dia]. */
function pascoa(ano: number): [number, number] {
  const a = ano % 19, b = Math.floor(ano / 100), c = ano % 100, d = Math.floor(b / 4), e = b % 4;
  const f = Math.floor((b + 8) / 25), g = Math.floor((b - f + 1) / 3), h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4), k = c % 4, l = (32 + 2 * e + 2 * i - h - k) % 7, m = Math.floor((a + 11 * h + 22 * l) / 451);
  const mes = Math.floor((h + l - 7 * m + 114) / 31), dia = ((h + l - 7 * m + 114) % 31) + 1;
  return [mes - 1, dia];
}

/**
 * DIAS ÚTEIS DO MÊS — Fluxo pelo memorial de cálculo (15/10/2026), para os juros por CDI
 * (`engine/juros.ts`): segunda a sexta, menos os feriados nacionais que caem em dia de semana.
 *
 * Feriados (o calendário que o mercado usa para o CDI): 1º/jan, 21/abr, 1º/mai, 7/set, 12/out,
 * 2/nov, 15/nov, 20/nov (nacional desde 2024), 25/dez; e os móveis — Carnaval (segunda e terça,
 * Páscoa −48 e −47), Sexta-feira Santa (Páscoa −2) e Corpus Christi (Páscoa +60).
 *
 * Pura e sem relógio: as datas são montadas em UTC, como em `addDays`.
 */
export function diasUteis(ym: Ym): number {
  const ano = Math.floor(ym / 12), mes = ym % 12;
  const dia0 = (mo: number, d: number) => Date.UTC(ano, mo, d);
  const [pm, pd] = pascoa(ano);
  const p = dia0(pm, pd), umDia = 86_400_000;
  const feriados = new Set<number>([
    dia0(0, 1), dia0(3, 21), dia0(4, 1), dia0(8, 7), dia0(9, 12), dia0(10, 2), dia0(10, 15), dia0(11, 25),
    ...(ano >= 2024 ? [dia0(10, 20)] : []),
    p - 48 * umDia, p - 47 * umDia, p - 2 * umDia, p + 60 * umDia,
  ]);
  const fim = new Date(Date.UTC(ano, mes + 1, 0)).getUTCDate();
  let n = 0;
  for (let d = 1; d <= fim; d++) {
    const t = dia0(mes, d), dow = new Date(t).getUTCDay();
    if (dow !== 0 && dow !== 6 && !feriados.has(t)) n++;
  }
  return n;
}
